import {useState} from 'react';
import {useQuery} from '@apollo/client';
import {Link} from 'react-router-dom';
import {Alert, Button, Radio, Table} from 'antd';
import {MailOutlined, ReloadOutlined} from '@ant-design/icons';
import {EmptyState, Section} from 'pages/home/shared/DashboardUI';
import {GET_SUPPORT_PERFORMANCE} from './resolvers';
import {usePerformanceFilters} from './usePerformanceFilters';
import {PerformanceFilterBar} from './PerformanceFilterBar';
import {ExportPerformanceModal} from './ExportPerformanceModal';
import {MetricCards, ResponseStatus} from './MetricCards';
import {TargetLink} from './TargetLink';
import {RankingTable, rankingFilterId, rankingLabel} from './RankingTable';
import {PerformanceHelp} from './PerformanceHelp';
import {
  UNATTRIBUTED,
  agentName,
  agentPath,
  compareNullable,
  describeRange,
  formatAverageRating,
  formatCount,
  formatCoverage,
  formatDuration,
  overviewInput,
  showUnattributed,
  writeFilters,
} from './performanceUtils';
import {Explained, Info, TERMS} from './Info';
import styles from './performance.module.scss';

// Each column reads one part of a row: `metrics` (the default) or
// `firstResponse`, which agents, Unattributed and Total all carry.
const metricColumns = [
  {
    title: <Explained text={TERMS.resolved}>Resolved</Explained>,
    key: 'resolved',
    get: m => m?.resolved,
  },
  {
    title: (
      <Explained
        text={`${TERMS.agentPending} The Total also counts escalated tickets still in the queue, which can keep an assignee after a reopen, so it can be higher than the rows added up.`}>
        Pending now
      </Explained>
    ),
    key: 'pending',
    get: m => m?.pending,
  },
  {
    title: <Explained text={TERMS.rating}>Avg rating</Explained>,
    key: 'averageRating',
    get: m => m?.averageRating,
    render: m =>
      m?.averageRating === null || m?.averageRating === undefined ? (
        <span className={styles.subtle}>No ratings</span>
      ) : (
        <span className={styles.twoLine}>
          <span>{formatAverageRating(m.averageRating)}</span>
          <span className={styles.smallNote}>
            {formatCoverage(m.ratingCoverage ?? 0)} rated
          </span>
        </span>
      ),
  },
  {
    title: <Explained text={TERMS.ratingCount}>Ratings</Explained>,
    key: 'ratingCount',
    get: m => m?.ratingCount,
  },
  {
    title: <Explained text={TERMS.firstResponse}>First response</Explained>,
    key: 'firstResponse',
    source: 'firstResponse',
    get: f => f?.averageSeconds,
    render: f =>
      f?.averageSeconds === null || f?.averageSeconds === undefined ? (
        <ResponseStatus firstResponse={f} />
      ) : (
        <span className={styles.twoLine}>
          {formatDuration(f.averageSeconds)}
          <ResponseStatus firstResponse={f} />
        </span>
      ),
  },
  {
    title: <Explained text={TERMS.customerWait}>Avg resolution time</Explained>,
    key: 'averageCustomerWaitSeconds',
    get: m => m?.averageCustomerWaitSeconds,
    render: m => duration(m?.averageCustomerWaitSeconds),
  },
  {
    title: <Explained text={TERMS.turnaround}>Avg queue time</Explained>,
    key: 'averageAgentSeconds',
    get: m => m?.averageAgentSeconds,
    render: m => duration(m?.averageAgentSeconds),
  },
  {
    title: (
      <Explained text={`Median human queue turnaround. ${TERMS.median}`}>
        Median queue time
      </Explained>
    ),
    key: 'medianAgentSeconds',
    get: m => m?.medianAgentSeconds,
    render: m => duration(m?.medianAgentSeconds),
  },
];

// A missing time reads "Not available", in grey so real numbers stand out.
const duration = seconds =>
  seconds === null || seconds === undefined ? (
    <span className={styles.subtle}>{formatDuration(seconds)}</span>
  ) : (
    formatDuration(seconds)
  );

const part = (column, row) => row?.[column.source || 'metrics'];

const cell = (column, row) =>
  column.render
    ? column.render(part(column, row))
    : formatCount(column.get(part(column, row)));

/** Whether the rankings count tickets opened or resolved in the period. */
const BasisToggle = ({value, update}) => (
  <Radio.Group
    size="small"
    optionType="button"
    value={value}
    onChange={event => update({basis: event.target.value})}
    options={[
      {value: 'OPENED', label: 'Opened'},
      {value: 'RESOLVED', label: 'Resolved'},
    ]}
  />
);

/**
 * The team report: headline numbers, the per-agent table and the
 * category → feature → issue rankings, for the filters in the URL.
 */
export const PerformanceOverview = () => {
  const {filters, update} = usePerformanceFilters();
  const [exportOpen, setExportOpen] = useState(false);
  const {data, loading, error, refetch} = useQuery(GET_SUPPORT_PERFORMANCE, {
    variables: {input: overviewInput(filters)},
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
  });

  const result = data?.getSupportPerformance;
  const report =
    result?.__typename === 'ResponseWithSupportPerformance'
      ? result.data
      : null;
  const errorMessage =
    result?.__typename === 'Error'
      ? result.message
      : error && !result
      ? "Couldn't load support performance."
      : null;

  const detailSearch = writeFilters({...filters, page: 1});
  const agentLink = (agentId, label) => (
    <Link className={styles.agentLink} to={agentPath(agentId, detailSearch)}>
      {label}
    </Link>
  );

  const agentRows = (report?.agents || []).map(row => ({
    key: row.agentId,
    agentId: row.agentId,
    name: agentName(row.agent, row.agentId),
    metrics: row.metrics,
    firstResponse: row.firstResponse,
  }));
  const unattributed =
    showUnattributed(filters.agents) && report?.unattributed
      ? report.unattributed
      : null;

  // Which ranking the drill-down is showing, and the trail back up.
  const level = filters.feature
    ? 'issues'
    : filters.category
    ? 'features'
    : 'categories';
  const categoryRow = (report?.categories || []).find(
    row => rankingFilterId(row) === filters.category,
  );
  const featureRow = (report?.features || []).find(
    row => rankingFilterId(row) === filters.feature,
  );
  const drillRows = report?.[level] || [];
  const verb = filters.basis === 'RESOLVED' ? 'resolved' : 'opened';
  // Shortcuts to the busiest categories; a category with nothing opened is
  // no shortcut.
  const topCategories = (report?.categories || [])
    .filter(row => row.count > 0)
    .slice(0, 3);

  return (
    <div className={styles.page}>
      <header className={styles.pageHead}>
        <div>
          <h2 className={styles.pageTitle}>Support performance</h2>
          <p className={styles.pageSubtitle}>
            {describeRange(filters.from, filters.to)} · all times in UTC
          </p>
        </div>
        <div className={styles.headActions}>
          <Button
            icon={<ReloadOutlined />}
            loading={loading && Boolean(report)}
            onClick={() => refetch()}>
            Refresh
          </Button>
          <Button icon={<MailOutlined />} onClick={() => setExportOpen(true)}>
            Email CSV
          </Button>
        </div>
      </header>

      <PerformanceFilterBar
        filters={filters}
        update={update}
        availableAgents={report?.availableAgents}
        showAgents
      />

      {errorMessage && (
        <Alert
          type="error"
          showIcon
          message={errorMessage}
          action={
            <Button size="small" onClick={() => refetch()}>
              Retry
            </Button>
          }
        />
      )}

      <MetricCards
        metrics={report?.overview}
        firstResponse={report?.firstResponse ?? null}
        pendingAsOf={report?.pendingAsOf}
        closedBy={report?.closedBy}
        loading={loading && !report}
        targetAction={<TargetLink />}
      />

      {!filters.category && topCategories.length > 0 && (
        <div className={styles.topCategories}>
          <span className={styles.subtle}>
            <Explained
              text={`The three categories with the most tickets ${verb} in the period. Click one to look inside it.`}>
              Busiest categories
            </Explained>
          </span>
          {topCategories.map(row => (
            <button
              type="button"
              key={`${row.id}-${row.language}-${row.product}`}
              className={styles.categoryChip}
              onClick={() => update({category: rankingFilterId(row)})}>
              {rankingLabel(row)}
              <span className={styles.chipCount}>{formatCount(row.count)}</span>
            </button>
          ))}
        </div>
      )}

      <Section
        title="Agents"
        description="Click a name for that agent's tickets and trends. Click a column heading to sort.">
        <div className={styles.tableScroll}>
          <Table
            size="small"
            rowKey="key"
            loading={loading}
            pagination={false}
            dataSource={agentRows}
            locale={{
              emptyText: (
                <EmptyState
                  compact
                  title="No agents in this view"
                  description="Nobody with a support role matches these filters."
                />
              ),
            }}
            columns={[
              {
                title: 'Agent',
                key: 'name',
                sorter: (a, b) => a.name.localeCompare(b.name),
                render: (_, row) => agentLink(row.agentId, row.name),
              },
              ...metricColumns.map(column => ({
                title: column.title,
                key: column.key,
                align: 'right',
                sorter: (a, b, order) =>
                  compareNullable(
                    column.get(part(column, a)),
                    column.get(part(column, b)),
                    order,
                  ),
                render: (_, row) => cell(column, row),
              })),
            ]}
            summary={() =>
              report ? (
                <Table.Summary>
                  {unattributed && (
                    <Table.Summary.Row className={styles.unattributedRow}>
                      <Table.Summary.Cell index={0}>
                        <span className={styles.twoLine}>
                          {agentLink(UNATTRIBUTED, 'Unattributed')}
                          <span className={styles.smallNote}>
                            AI, FAQ and customer closes{' '}
                            <Info text={TERMS.unattributed} />
                          </span>
                        </span>
                      </Table.Summary.Cell>
                      {metricColumns.map((column, index) => (
                        <Table.Summary.Cell
                          key={column.key}
                          index={index + 1}
                          align="right">
                          {cell(column, unattributed)}
                        </Table.Summary.Cell>
                      ))}
                    </Table.Summary.Row>
                  )}
                  <Table.Summary.Row className={styles.totalRow}>
                    <Table.Summary.Cell index={0}>
                      <Explained text={TERMS.total}>Total</Explained>
                    </Table.Summary.Cell>
                    {metricColumns.map((column, index) => (
                      <Table.Summary.Cell
                        key={column.key}
                        index={index + 1}
                        align="right">
                        {cell(column, {
                          metrics: report.overview,
                          firstResponse: report.firstResponse,
                        })}
                      </Table.Summary.Cell>
                    ))}
                  </Table.Summary.Row>
                </Table.Summary>
              ) : null
            }
          />
        </div>
      </Section>

      <Section
        title={`${
          level === 'categories'
            ? 'Top categories'
            : level === 'features'
            ? 'Features'
            : 'Issues'
        } ${verb}`}
        description={`Tickets ${verb} in the period. Click a row to see what is inside it.`}
        actions={
          <div className={styles.headActions}>
            <BasisToggle value={filters.basis} update={update} />
            {filters.category ? (
              <nav className={styles.breadcrumb} aria-label="Ranking level">
                <button type="button" onClick={() => update({category: null})}>
                  All categories
                </button>
                <span>›</span>
                {filters.feature ? (
                  <button type="button" onClick={() => update({feature: null})}>
                    {categoryRow ? rankingLabel(categoryRow) : 'Category'}
                  </button>
                ) : (
                  <strong>
                    {categoryRow ? rankingLabel(categoryRow) : 'Category'}
                  </strong>
                )}
                {filters.feature && (
                  <>
                    <span>›</span>
                    <strong>
                      {featureRow ? rankingLabel(featureRow) : 'Feature'}
                    </strong>
                  </>
                )}
              </nav>
            ) : null}
          </div>
        }>
        <RankingTable
          rows={drillRows}
          basis={filters.basis}
          loading={loading}
          onSelect={
            level === 'categories'
              ? row => update({category: rankingFilterId(row)})
              : level === 'features'
              ? row => update({feature: rankingFilterId(row)})
              : undefined
          }
        />
      </Section>

      {level !== 'issues' && (
        <Section
          title={`Top issues ${verb}`}
          description={
            filters.basis === 'RESOLVED'
              ? 'The problems the team closed most in the period.'
              : 'The most common problems customers raised in the period.'
          }>
          <RankingTable
            rows={report?.issues}
            basis={filters.basis}
            loading={loading}
            showAncestry
          />
        </Section>
      )}

      <PerformanceHelp />

      <ExportPerformanceModal
        isOpen={exportOpen}
        onClose={() => setExportOpen(false)}
        filters={filters}
      />
    </div>
  );
};
